import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";

const LAST_UPDATED = "18 June 2026";

export default function Terms() {
 return (
 <div className="min-h-screen bg-[#FAF7F0]">
 <Navbar />
 <main className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16" data-testid="terms-page">
 <div className="mb-10">
 <div className="label-mono text-[#1B6C62] mb-3">Legal</div>
 <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">Terms of Service</h1>
 <p className="text-sm text-[#6B7280] font-mono">Last updated: {LAST_UPDATED}</p>
 </div>

 <article className="space-y-8 text-[15px] text-[#1A1A1A] leading-relaxed font-serif">
 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">1. Plain-English summary</h2>
 <p>These terms govern your use of TenantTrail. The most important things to understand up front:</p>
 <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
 <li>TenantTrail provides <strong>legal information and document organisation tools</strong>, not legal advice. We are not your solicitor.</li>
 <li>You are responsible for the accuracy of the information you upload.</li>
 <li>You own your data. You can export and delete it whenever you want.</li>
 <li>Paid subscriptions are billed in AUD via Stripe and can be cancelled at any time.</li>
 <li>Nothing in these terms removes any non-excludable rights you have under the Australian Consumer Law.</li>
 </ul>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">2. Who can use TenantTrail</h2>
 <p>You must be at least 18 years old and capable of entering into a binding contract. You must provide accurate information when you create your account, and you must keep your password confidential. You are responsible for all activity that happens under your account.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">3. Not legal advice</h2>
 <p>TenantTrail is an information service. The content we publish, the AI guidance the platform generates, the letters we help you draft, and the case-law references we surface are <strong>legal information</strong>, not legal advice. We do not establish a solicitor-client relationship.</p>
 <p className="mt-3">For advice specific to your situation we recommend you consult a qualified solicitor or your state&apos;s Tenants&apos; Union / community legal centre.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">4. Your content</h2>
 <p>You retain full ownership of everything you upload: documents, photographs, voice notes, letters, and case content. By uploading content you grant TenantTrail a limited, non-exclusive licence to store, process, and display that content solely to provide the service to you.</p>
 <p className="mt-3">You agree not to upload content that:</p>
 <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
 <li>Belongs to someone else without their permission.</li>
 <li>Is unlawful, defamatory, or designed to harass.</li>
 <li>Contains malware.</li>
 </ul>
 <p className="mt-3">We may remove content that breaches these rules. We will give you reasonable notice unless doing so would itself be unlawful.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">5. Acceptable use</h2>
 <p>You agree not to:</p>
 <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
 <li>Reverse engineer or attempt to extract our source code or AI prompts.</li>
 <li>Use the service to send unsolicited bulk email or harass a landlord, agent, or any third party.</li>
 <li>Interfere with the security or integrity of the service.</li>
 <li>Resell or sublicense access to the service without our written consent.</li>
 </ul>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">6. Subscriptions and payment</h2>
 <p>Paid plans (Vault, Shield, Dispute Mode, Pro, 3-Year Shield Pass) are billed in Australian Dollars via Stripe. Monthly plans renew every 30 days. Annual plans renew every 365 days. Dispute Mode is a one-off 30-day purchase. The 3-Year Shield Pass is a one-off purchase that grants Shield-tier features and allowances for 1,095 days from the date of payment. After the 3 years the Pass lapses to the standard monthly Shield plan unless you cancel. By subscribing you authorise us, via Stripe, to charge the payment method you provided.</p>
 <p className="mt-3">You can cancel renewal at any time from your account settings. Cancellation takes effect at the end of your current billing period. We do not refund partial periods unless the law requires it.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">7. Refunds and consumer guarantees</h2>
 <p>Under the Australian Consumer Law you are entitled to a refund, replacement, or repair if a service we supply is not of acceptable quality, fit for purpose, or does not match its description. These rights cannot be excluded by these terms.</p>
 <p className="mt-3">If you are unhappy with the service please contact us at <a href="mailto:support@tenanttrail.net" className="text-[#1B6C62] underline">support@tenanttrail.net</a>. We will work with you in good faith.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">8. AI-generated content</h2>
 <p>TenantTrail uses third-party AI (Anthropic Claude, OpenAI Whisper) to generate guidance, summaries, and draft letters. AI output:</p>
 <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
 <li>Can contain errors. Always read it carefully before relying on it.</li>
 <li>Is not legal advice (see clause 3).</li>
 <li>Must not be presented to a tribunal as the unedited word of a qualified lawyer.</li>
 </ul>
 <p className="mt-3">You are responsible for the content of any letter you send, including those drafted with AI assistance. By sending a letter through TenantTrail you confirm you have read and agreed with its contents.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">9. Property history lookup</h2>
 <p>Our &ldquo;Check a rental&rdquo; tool surfaces deep-links into public Australian registers (AustLII, state Fair Trading licensee registers, ASIC). We make no factual claim about any property, lessor, or agent. We point you to public sources for you to read yourself. We accept no responsibility for the accuracy of those external sources.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">10. Service availability</h2>
 <p>We aim for high uptime but do not guarantee that the service will be uninterrupted or error-free. We may carry out maintenance, sometimes with little notice, particularly for security fixes. We are not liable for losses arising from temporary unavailability.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">11. Liability</h2>
 <p>To the maximum extent permitted by law, our total liability to you for any claim arising out of your use of the service is limited to the fees you paid us in the twelve months before the claim arose. Nothing in this clause limits any liability that cannot be excluded under the Australian Consumer Law.</p>
 <p className="mt-3">We are not liable for the outcome of any tribunal proceeding or any decision made by a landlord, agent, or court. You bear sole responsibility for the conduct of your tenancy matter.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">12. Termination</h2>
 <p>You can close your account at any time from your account settings or by emailing us. We may suspend or close accounts that materially breach these terms, are inactive for more than two years, or that we reasonably believe are being used for unlawful purposes.</p>
 <p className="mt-3">On termination we will preserve your evidence for a reasonable window so you can export it before final deletion.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">13. Changes to these terms</h2>
 <p>We may update these terms occasionally. If a change materially reduces your rights we will email you at least 14 days before it takes effect. Continued use after the effective date constitutes acceptance.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">14. Governing law</h2>
 <p>These terms are governed by the laws of Queensland, Australia. The courts of Queensland have non-exclusive jurisdiction.</p>
 </section>

 <section>
 <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">15. Contact</h2>
 <p>Questions about these terms can be sent to <a href="mailto:support@tenanttrail.net" className="text-[#1B6C62] underline">support@tenanttrail.net</a>.</p>
 </section>
 </article>

 <div className="mt-12 pt-8 border-t border-[#E5E4DE] text-sm text-[#6B7280] font-serif italic">
 <p>See also: <Link to="/privacy" className="text-[#0D2B52] underline">Privacy Policy</Link></p>
 </div>
 
      <SiteFooter />
      </main>
 </div>
 );
}
